import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Button,
  CardComponent,
  CareNavigatorCard,
  NoDataVisual,
  TextField,
  Trash,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React, { useEffect, useState } from 'react';
import CareNavigatorsTabMessages from '../../../core-utils/messages/CareNavigatorConstants';
import Loader from '../../molecules/Loader';
import { formatCareNavigatorCardData } from '../../../core-utils/helper-utils/careNavigatorHelper';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import axios from 'axios';
import properties from '../../../core-utils/properties';
import { Pagination } from '@mui/material';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import Snackbar from '../../atoms/Snackbar';
import CreateCareNavModal from '../CreateCareNavModal';
import { useNavigate } from 'react-router-dom';
import {
  CARE_NAVIGATOR,
  CARE_NAVIGATOR_PROFILE,
} from '../../../core-utils/routes';
import { useStores } from '../../../stores';
import {
  SimpleSearch,
  TextWithIcon,
  Typography,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';

export interface CareNavigatorsTabProps {}

const CareNavigatorsTab: React.FC<CareNavigatorsTabProps> = ({}) => {
  const useStyles = makeStyles()({
    careNavCards: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      padding: '20px 20px',
      gap: '20px 20px',
    },
    saveButton: {
      padding: '9px 16px',
      width: '195px',
      height: '38px',
      border: '1px solid ' + patientAccessTheme.palette.primary[500],
      borderRadius: '4px',
      textTransform: 'none',
      fontSize: '12px',
      lineHeight: '12px',
      fontWeight: 500,
    },
    emptyData: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      padding: '20px',
    },
    pagination: {
      display: 'flex',
      justifyContent: 'right',
      marginTop: '16px',
    },
    topRightItem: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '10px',
    },
  });

  const messages =
    CareNavigatorsTabMessages.CARE_NAVIGATOR_PAGE.CARE_NAVIGATORS_DATA;

  const [loader] = useState<any>(<Loader />);
  const [loading, setLoading] = useState<boolean>(false);
  const [careNavigators, setCareNavigators] = useState<any[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isAddCareNav, setIsAddCareNav] = useState<boolean>(false);
  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });
  const [groups, setGroups] = useState<any[]>([]);
  const [assignData, setAssignData] = useState<any>({
    firstName: '',
    lastName: '',
    role: '',
    email: '',
    contact: '',
    experience: '',
    speciality: '',
    groups: [],
    features: [],
  });

  const handleCloseSnackBar = () => {
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  const { classes } = useStyles();
  const navigate = useNavigate();
  const memberSummaryStore = useStores().memberSummaryStore;

  const handleAddNavigator = () => {
    setIsAddCareNav(true);
    return;
  };

  const addCareNavigatorToGroups = async (
    groups: any,
    email: string,
    name: string,
    role: string,
  ) => {
    return await axios.post(
      properties.aiqBaseUrl +
        properties.groupsBaseURL +
        properties.addCareNavigatorToGroups,
      {
        groups: groups,
        email: email,
        name: name,
        role: role,
      },
    );
  };

  const sendMail = async (email: string) => {
    return await axios.post(
      properties.userManagementBaseUrl +
        properties.careNavigator +
        properties.sendEmail,
      {
        forgotPassword: true,
        redirectUrl: '/reset-password',
        email: email,
      },
    );
  };

  const handleSave = async () => {
    const features = assignData.features?.filter(
      (item: any) => item.value !== 'CARE_NAVIGATION',
    );
    await axios
      .post(properties.userManagementBaseUrl + properties.careNavigator, {
        ...assignData,
        features: [
          'CARE_NAVIGATION',
          ...features?.map((item: any) => item.value),
        ],
        groups: assignData.groups.map((group: any) => ({
          groupId: group.id,
          groupName: group.name,
        })),
      })
      .then((response) => {
        if (response.status === 200) {
          sendMail(assignData.email);
          addCareNavigatorToGroups(
            assignData.groups,
            assignData.email,
            assignData.firstName + ' ' + assignData.lastName,
            assignData.role,
          );
          handleOpenSnackBar(
            'Care Navigator added succesfully',
            'success',
            6000,
          );
        }
        setIsAddCareNav(false);
        apiCalls();
      })
      .catch((error) => {
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000);
      });
  };

  const getCareNavigatorGroups = async () => {
    return await axios.post(
      properties.aiqBaseUrl +
        properties.groupsBaseURL +
        properties.getCareNavigatorGroups,
    );
  };

  const handleCareNavigatorCardClick = (_id: string) => {
    memberSummaryStore.removePathDetails();
    const pathDetails: any[] = [];
    pathDetails.push([
      'text',
      'Care Navigator',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => navigate(CARE_NAVIGATOR),
    ]);

    memberSummaryStore.setPathDetails(pathDetails);

    navigate(CARE_NAVIGATOR_PROFILE, {
      state: {
        care_navigator_id: _id,
      },
    });
  };

  const handleMenuClick = async (menu: string, cn_id: string) => {
    await axios
      .delete(
        properties.userManagementBaseUrl +
          properties.careNavigator +
          '/' +
          cn_id,
      )
      .then((response) => {
        if (response.status === 200) {
          handleOpenSnackBar(
            'Care Navigator deleted succesfully',
            'success',
            6000,
          );
        }
        apiCalls();
      })
      .catch((error) => {
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000);
      });
  };

  const handleSearch = async (value: string) => {
    if (value === '') {
      await Promise.all([getCareNavigators('')]).then((responses: any) => {
        responses.map((res: any) => {
          if (res?.status !== 200) {
            handleOpenSnackBar(res?.reason?.message, 'error', 6000);
          }
        });

        if (responses[0]?.status === 200) {
          setCareNavigators(responses[0]?.data?.data?.careNavigators);
          setTotalPages(responses[0]?.data?.data?.totalPages ?? 0);
        }
      });
    }

    if (!value || value.length <= 2) {
      return;
    }

    await Promise.all([getCareNavigators(value)]).then((responses: any) => {
      responses.map((res: any) => {
        if (res?.status !== 200) {
          handleOpenSnackBar(res?.reason?.message, 'error', 6000);
        }
      });

      if (responses[0]?.status === 200) {
        setCareNavigators(responses[0]?.data?.data?.careNavigators);
        setTotalPages(responses[0]?.data?.data?.totalPages ?? 0);
      }
    });
  };

  const getCareNavigators = async (value = '') => {
    return await axios.post(
      properties.aiqBaseUrl +
        properties.groupsBaseURL +
        properties.getCareNavigators,
      {
        page: pageNumber - 1,
        pageSize: 6,
        searchValue: value,
      },
    );
  };

  const apiCalls = async () => {
    setLoading(true);
    await Promise.all([getCareNavigators(), getCareNavigatorGroups()])
      .then((responses: any) => {
        responses.map((res: any) => {
          if (res?.status !== 200) {
            handleOpenSnackBar(res?.reason?.message, 'error', 6000);
          }
        });

        if (responses[0]?.status === 200) {
          setCareNavigators(responses[0]?.data?.data?.careNavigators);
          setTotalPages(responses[0]?.data?.data?.totalPages ?? 0);
        }

        if (responses[1]?.status === 200) {
          setGroups(responses[1]?.data?.data);
        }
      })
      .then(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    apiCalls();
  }, [pageNumber]);

  const isSaveButtonDisabled = () => {
    if (
      assignData.firstName === '' ||
      assignData.lastName === '' ||
      assignData.email === '' ||
      assignData.role === '' ||
      assignData.contact === ''
    ) {
      return true;
    }
    return false;
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPageNumber(value);
  };

  return (
    <Grid>
      {loading ? (
        loader
      ) : (
        <Grid>
          <CardComponent
            title={messages.title}
            subTitle={messages.subTitle}
            width="1176px"
            mainGridStyles={{ padding: '20px' }}
            body={
              careNavigators?.length === 0 ? (
                <Grid className={classes.emptyData}>
                  <NoDataVisual content="No Data Available" height="200px" />
                </Grid>
              ) : (
                <Grid>
                  <Grid className={classes.careNavCards}>
                    {careNavigators?.map((item) => (
                      <CareNavigatorCard
                        {...formatCareNavigatorCardData(item)}
                        height="280px"
                        width="525px"
                        detailsStyles={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 1fr)',
                        }}
                        handleCareNavClick={() => {
                          handleCareNavigatorCardClick(item?.cn_id);
                        }}
                        handleMenuOnClick={(id: string) => {
                          handleMenuClick(id, item.cn_id);
                        }}
                        options={[
                          {
                            text: (
                              <TextWithIcon
                                label={
                                  <Typography
                                    fontSize="14px"
                                    lineHeight="160%"
                                    fontType="medium"
                                    textColor={
                                      patientAccessTheme.palette.text
                                        .highemphasis
                                    }
                                  >
                                    Delete
                                  </Typography>
                                }
                                startIcon={<Trash />}
                              />
                            ),
                            action: 'Delete',
                          },
                        ]}
                      />
                    ))}
                  </Grid>
                  <div className={classes.pagination}>
                    <Pagination
                      page={pageNumber}
                      count={totalPages}
                      onChange={handlePageChange}
                      size="small"
                      color="primary"
                    />
                  </div>
                </Grid>
              )
            }
            topRightItem={
              <Grid className={classes.topRightItem}>
                <TextField
                  placeholder={'Search'}
                  height={'36px'}
                  width={'305px'}
                  startAdornment={
                    <SimpleSearch
                      height="20px"
                      width="20px"
                      stroke={patientAccessTheme.palette.text.lowemphasis}
                    />
                  }
                  onChange={(event: any) => handleSearch(event.target.value)}
                  showStartAdornment={true}
                  style={{ marginRight: '30px' }}
                />
                <Button
                  label={'Add Care Navigator'}
                  variant="contained"
                  className={classes.saveButton}
                  disabled={false}
                  onClick={() => handleAddNavigator()}
                  width="175px"
                />
              </Grid>
            }
          />
        </Grid>
      )}
      {isAddCareNav && (
        <CreateCareNavModal
          open={isAddCareNav}
          handleClose={() => setIsAddCareNav(false)}
          handleSave={handleSave}
          isSaveButtonDisabled={isSaveButtonDisabled()}
          isEditModal={false}
          assignCareNavState={[assignData, setAssignData]}
          groups={groups}
        />
      )}
      <Snackbar
        {...snackbarState}
        vertical={'bottom'}
        horizontal={'right'}
        handleClose={handleCloseSnackBar}
      />
    </Grid>
  );
};
export default CareNavigatorsTab;
